import { useAuthUser } from "Components/Auth/hooks/useAuthUser";
import { useCompany } from "Components/Company/hooks/useCompany";
import { getMemberRole } from "Components/Members/helpers/getMemberRole";
import { useProfile } from "Components/Profile/hooks/useProfile";
import {
	InviteState,
	isProject,
	Member,
	Project,
	ProjectType,
	SchedulePhase,
} from "domain-entities";
import { DEFAULT_COLOR_TAG } from "Helpers/ColorHelper";
import moment, { Moment } from "moment";
import { v4 } from "uuid";

export function getMembersAlphabetical(
	members: { [userId: string]: Member },
	projectName: string
) {
	const membersAlphabetical: { [emailAndIndex: string]: string } = {};

	Object.keys(members).forEach((email, index) => {
		const member = members[email];
		const lowerCaseProjectName = projectName.toLowerCase();

		if (member.id) {
			membersAlphabetical[member.id] = lowerCaseProjectName;
		}
	});

	return membersAlphabetical;
}

export function createProject(): Project | void {
	const user = useAuthUser.getState().user;
	const profile = useProfile.getState().item;
	const company = useCompany.getState().item;
	const memberRole = getMemberRole({ company, userId: user?.uid });

	if (!user) return;
	if (!profile) return;
	if (!company) return;
	if (!memberRole) return;

	return {
		colorTag: DEFAULT_COLOR_TAG,
		company: company.id,
		creationDate: moment().unix(),
		creator: user.uid,
		id: v4(),
		lastEditedDate: moment().unix(),
		lastOpenedDate: moment().unix(),
		members: {
			[profile.email]: {
				email: profile.email,
				id: user.uid,
				lastname: profile.lastname,
				name: profile.name,
				role: memberRole,
				inviteState: InviteState.ACCEPTED,
			},
		},
		membersActive: [user.uid],
		membersAlphabetical: {
			// "membersAlphabetical" is set inside "ProjectForm.tsx"
		},
		membersAlphabeticalArchived: {},
		membersCreated: {
			[user.uid]: moment().unix(),
		},
		membersCreatedArchived: {},
		membersLastOpened: {
			[user.uid]: moment().unix(),
		},
		membersLastOpenedArchived: {},
		name: "",
		projectType: ProjectType.PROJECT,
		unreadCounts: {
			[user.uid]: 0,
		},
		status: {
			name: "active",
			setAt: moment().unix(),
			setBy: user.uid,
		},
	};
}

export function getProjectsBetweenMoments(
	projects: Project[],
	schedulePhases: SchedulePhase[],
	start: Moment,
	end: Moment
) {
	const visibleSchedulePhases = schedulePhases.filter((phase) => {
		return (
			moment.unix(phase.startDate).isBefore(end) &&
			moment.unix(phase.endDate).isSameOrAfter(start)
		);
	});

	const uniqueProjectIds = visibleSchedulePhases
		.map((phase) => phase.projectId)
		.filter((projectId, index, array) => array.indexOf(projectId) === index);

	return projects.filter((project) => uniqueProjectIds.includes(project.id));
}

export function getProjectAddressString(project: Project) {
	return [project.street, project.zipcode, project.city]
		.filter((value) => value)
		.join(", ")
		.trim();
}

export function getProjectContactsString(project: Project) {
	return (project.contacts || [])
		.map((contact) => contact.name)
		.join(", ")
		.trim();
}

export interface ProjectTree {
	project?: Project;
	children: Project[];
}

function sortProjectsByName(a: Project, b: Project) {
	const aName = a.name.toLowerCase().trim();
	const bName = b.name.toLowerCase().trim();

	return aName.localeCompare(bName);
}

export function getProjectTrees(projectsAndFolders: Project[]) {
	const projectTrees: ProjectTree[] = [];
	let projects = [...projectsAndFolders]
		.filter((project) => project.projectType === ProjectType.PROJECT)
		.sort(sortProjectsByName);

	const folders = [...projectsAndFolders]
		.filter((project) => project.projectType === ProjectType.FOLDER)
		.sort(sortProjectsByName);

	folders.forEach((folder) => {
		const children = projects.filter(
			(project) => project.parentProject === folder.id
		);

		projects = projects.filter((project) => !children.includes(project));

		projectTrees.push({
			project: folder,
			children,
		});
	});

	projectTrees.push({
		children: [...projects],
	});

	return projectTrees;
}

export function useIsProjectRedirectable(
	projectOrProjectId?: string | Project,
	projects?: Project[]
) {
	const userId = useAuthUser((state) => state.user?.uid);

	function isProjectRedirectable(userId: string, project?: Project) {
		return (
			!!project?.membersLastOpened?.[userId] ||
			!!project?.membersLastOpenedArchived?.[userId]
		);
	}

	if (projectOrProjectId && userId) {
		if (isProject(projectOrProjectId)) {
			return isProjectRedirectable(userId, projectOrProjectId);
		} else if (projects) {
			const projectId = projectOrProjectId;
			const project = projects.find((item) => item.id === projectId);

			return isProjectRedirectable(userId, project);
		}
	} else {
		return false;
	}
}
